import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { citOffsetOverPaidItems, formatCitQty, withCitAutoStatus } from './citList';
import {
  createCitClearingInvoiceRow,
  createCitDepositLine,
  createCitDocumentRow,
  createCitItemRow,
  createCitLinkedReceipt,
  createCitOffsetLine,
  createCitOffsetRow,
  EMPTY_CIT_FORM,
} from '../types/applicationForm';
import type {
  ApplicationSubmission,
  CitAttachmentRef,
  CitClearingInvoiceRow,
  CitDepositLine,
  CitDocumentRow,
  CitItemRow,
  CitLinkedReceipt,
  CitOffsetLine,
  CitOffsetRow,
  CitTransferStatus,
  CustomiseItemsTransferFormData,
} from '../types/applicationForm';

const TABLE = 'cit_submissions';
const FORM_SLUG = 'customise-items-transfer' as const;
const BUCKET = 'cit-attachments';

/**
 * Full form mandatory checks (customer, items, deposits, etc.).
 * Keep true for normal use; set false only for bulk historical import.
 */
export const CIT_ENFORCE_MANDATORY = true;

/**
 * Supporting files for sales confirmation and deposit invoice.
 * Payment receipt / offset invoice supporting uploads stay optional for now.
 */
export const CIT_REQUIRE_SUPPORTING_FILES = true;

/** Payment receipt supporting file — optional until historical docs are available. */
export const CIT_REQUIRE_PAYMENT_SUPPORTING_FILE = false;

/** Offset invoice supporting file — optional until historical docs are available. */
export const CIT_REQUIRE_OFFSET_SUPPORTING_FILE = false;

/**
 * Keep offset Upload/Replace visible even when the offset section/row is locked,
 * so supporting docs can be added for older records without unlocking the whole section.
 */
export const CIT_ALLOW_LOCKED_OFFSET_FILE_UPLOAD = true;

/** Temporary: any Forms allowlisted user can open/create CIT (skip department submit gate in UI). */
export const CIT_OPEN_ACCESS = false;

function withCitSlug(row: ApplicationSubmission): ApplicationSubmission {
  return {
    ...row,
    form_slug: FORM_SLUG,
    form_data: normalizeCitFormData(row.form_data),
  };
}

function normalizeAttachment(raw: unknown): CitAttachmentRef | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as Partial<CitAttachmentRef>;
  const path = String(data.path ?? '').trim();
  const fileName = String(data.fileName ?? '').trim();
  if (!path) return undefined;
  return { path, fileName: fileName || path.split('/').pop() || 'attachment' };
}

function normalizeItemRow(raw: unknown): CitItemRow {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<CitItemRow>;
  return createCitItemRow({
    id: String(data.id ?? '').trim() || undefined,
    item_no: String(data.item_no ?? ''),
    item_no_transfer: String(data.item_no_transfer ?? ''),
    description: String(data.description ?? ''),
    sales_confirmation_qty: String(data.sales_confirmation_qty ?? ''),
    uom: String(data.uom ?? ''),
  });
}

function normalizeOffsetLine(raw: unknown): CitOffsetLine {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<CitOffsetLine>;
  const warehouse_defu_qty = String(data.warehouse_defu_qty ?? '');
  const warehouse_depot_qty = String(data.warehouse_depot_qty ?? '');
  const defu = Number(warehouse_defu_qty || 0);
  const depot = Number(warehouse_depot_qty || 0);
  const hasWh = Boolean(warehouse_defu_qty.trim() || warehouse_depot_qty.trim());
  const sum = (Number.isFinite(defu) ? defu : 0) + (Number.isFinite(depot) ? depot : 0);
  const quantity = hasWh
    ? Number.isInteger(sum)
      ? String(sum)
      : String(Math.round(sum * 1000) / 1000)
    : String(data.quantity ?? '');
  return createCitOffsetLine({
    id: String(data.id ?? '').trim() || undefined,
    item_no: String(data.item_no ?? ''),
    quantity,
    warehouse_defu_qty,
    warehouse_depot_qty,
  });
}

function normalizeOffsetRow(raw: unknown): CitOffsetRow {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<CitOffsetRow> & {
    file?: unknown;
    stock_transfer_form?: unknown;
    item_no?: string;
    quantity?: string;
    warehouse_defu_qty?: string;
    warehouse_depot_qty?: string;
    lines?: unknown;
  };

  let lines: CitOffsetLine[] = [];
  if (Array.isArray(data.lines) && data.lines.length > 0) {
    lines = data.lines.map(normalizeOffsetLine);
  } else if (
    data.item_no ||
    data.quantity ||
    data.warehouse_defu_qty ||
    data.warehouse_depot_qty
  ) {
    // Legacy flat offset → single line.
    lines = [
      createCitOffsetLine({
        item_no: String(data.item_no ?? ''),
        quantity: String(data.quantity ?? ''),
        warehouse_defu_qty: String(data.warehouse_defu_qty ?? ''),
        warehouse_depot_qty: String(data.warehouse_depot_qty ?? ''),
      }),
    ];
  } else {
    lines = [createCitOffsetLine()];
  }

  return createCitOffsetRow({
    id: String(data.id ?? '').trim() || undefined,
    number: String(data.number ?? ''),
    invoice_amount: String(data.invoice_amount ?? ''),
    file: normalizeAttachment(data.file),
    stock_transfer_no: String(data.stock_transfer_no ?? ''),
    transfer_reason: String(data.transfer_reason ?? ''),
    stock_transfer_form: normalizeAttachment(data.stock_transfer_form),
    lines,
  });
}

function offsetLineHasAny(line: CitOffsetLine): boolean {
  return Boolean(
    line.item_no.trim() ||
      line.quantity.trim() ||
      line.warehouse_defu_qty.trim() ||
      line.warehouse_depot_qty.trim(),
  );
}

function offsetRowHasAny(
  row: CitOffsetRow,
  pendingFile?: File | null,
): boolean {
  return Boolean(
    row.number.trim() ||
      row.invoice_amount.trim() ||
      row.file ||
      pendingFile ||
      row.stock_transfer_no.trim() ||
      row.lines.some(offsetLineHasAny),
  );
}

function normalizeClearingInvoiceRow(raw: unknown): CitClearingInvoiceRow {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<CitClearingInvoiceRow> & {
    file?: unknown;
  };
  return createCitClearingInvoiceRow({
    id: String(data.id ?? '').trim() || undefined,
    number: String(data.number ?? ''),
    file: normalizeAttachment(data.file),
  });
}

function clearingInvoiceHasAny(
  row: CitClearingInvoiceRow,
  pendingFile?: File | null,
): boolean {
  return Boolean(row.number.trim() || row.file || pendingFile);
}

function normalizeDepositLine(raw: unknown): CitDepositLine {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<CitDepositLine>;
  return createCitDepositLine({
    id: String(data.id ?? '').trim() || undefined,
    item_no: String(data.item_no ?? ''),
    quantity: String(data.quantity ?? ''),
  });
}

function depositLineHasAny(line: CitDepositLine): boolean {
  return Boolean(line.item_no.trim() || line.quantity.trim());
}

function normalizeLinkedReceipt(raw: unknown): CitLinkedReceipt {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<CitLinkedReceipt> & {
    file?: unknown;
  };
  return createCitLinkedReceipt({
    id: String(data.id ?? '').trim() || undefined,
    number: String(data.number ?? ''),
    file: normalizeAttachment(data.file),
  });
}

function normalizeDocumentRow(raw: unknown): CitDocumentRow {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<CitDocumentRow> & {
    file?: unknown;
    quantity?: string;
    payment_receipt_no?: string;
    payment_receipt_file?: unknown;
    payment_receipts?: unknown;
    lines?: unknown;
  };

  let payment_receipts: CitLinkedReceipt[] = [];
  if (Array.isArray(data.payment_receipts) && data.payment_receipts.length > 0) {
    payment_receipts = data.payment_receipts.map(normalizeLinkedReceipt);
  } else if (data.payment_receipt_no || data.payment_receipt_file) {
    payment_receipts = [
      createCitLinkedReceipt({
        number: String(data.payment_receipt_no ?? ''),
        file: normalizeAttachment(data.payment_receipt_file),
      }),
    ];
  } else {
    payment_receipts = [createCitLinkedReceipt()];
  }

  let lines: CitDepositLine[] = [];
  if (Array.isArray(data.lines) && data.lines.length > 0) {
    lines = data.lines.map(normalizeDepositLine);
  } else if (String(data.quantity ?? '').trim()) {
    // Legacy single quantity → one line (item filled in later from General).
    lines = [createCitDepositLine({ quantity: String(data.quantity ?? '') })];
  } else {
    lines = [createCitDepositLine()];
  }

  return createCitDocumentRow({
    id: String(data.id ?? '').trim() || undefined,
    number: String(data.number ?? ''),
    invoice_amount: String(data.invoice_amount ?? ''),
    file: normalizeAttachment(data.file),
    payment_receipts,
    lines,
  });
}

/** Supports current shape and legacy single-item / separate payment_receipts records. */
export function normalizeCitFormData(raw: unknown): CustomiseItemsTransferFormData {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const statusRaw = String(data.status ?? '').trim();
  const status: CitTransferStatus =
    statusRaw === 'New' ||
    statusRaw === 'Processing' ||
    statusRaw === 'Completed' ||
    statusRaw === 'Cancelled'
      ? statusRaw
      : statusRaw === 'Pending'
        ? 'New'
        : statusRaw === 'Partial'
          ? 'Processing'
          : EMPTY_CIT_FORM.status;

  let items: CitItemRow[] = [];
  if (Array.isArray(data.items) && data.items.length > 0) {
    items = data.items.map(normalizeItemRow);
  } else if (data.item_no || data.uom || data.sales_confirmation_qty) {
    items = [
      createCitItemRow({
        item_no: String(data.item_no ?? ''),
        description: String(data.description ?? ''),
        sales_confirmation_qty: String(data.sales_confirmation_qty ?? ''),
        uom: String(data.uom ?? ''),
      }),
    ];
  } else {
    items = [createCitItemRow()];
  }

  const needsRaw = data.needs_deposit;
  let needs_deposit: boolean | null =
    needsRaw === true || needsRaw === 'true' || needsRaw === 1 || needsRaw === '1'
      ? true
      : needsRaw === false || needsRaw === 'false' || needsRaw === 0 || needsRaw === '0'
        ? false
        : null;

  const import_source: 'manual' | 'powerbi' =
    data.import_source === 'powerbi' ? 'powerbi' : 'manual';
  const pbi_synced_at = data.pbi_synced_at
    ? String(data.pbi_synced_at)
    : null;

  let deposit_invoices: CitDocumentRow[] = [];
  if (Array.isArray(data.deposit_invoices) && data.deposit_invoices.length > 0) {
    deposit_invoices = data.deposit_invoices.map(normalizeDocumentRow);
  } else if (data.deposit_invoice_no || data.deposit_invoice_file) {
    deposit_invoices = [
      createCitDocumentRow({
        number: String(data.deposit_invoice_no ?? ''),
        file: normalizeAttachment(data.deposit_invoice_file),
      }),
    ];
  } else if (needs_deposit === true) {
    deposit_invoices = [createCitDocumentRow()];
  } else {
    deposit_invoices = [];
  }

  // Legacy top-level payment_receipts[] — zip onto deposits that still have empty receipts.
  if (Array.isArray(data.payment_receipts) && data.payment_receipts.length > 0) {
    const legacy = data.payment_receipts as unknown[];
    deposit_invoices = deposit_invoices.map((row, index) => {
      const hasFilled = row.payment_receipts.some(
        (receipt) => receipt.number.trim() || receipt.file,
      );
      if (hasFilled) return row;
      const receipt = legacy[index];
      if (!receipt || typeof receipt !== 'object') return row;
      return {
        ...row,
        payment_receipts: [normalizeLinkedReceipt(receipt)],
      };
    });
  }

  let offset_invoices: CitOffsetRow[] = [];
  if (Array.isArray(data.offset_invoices) && data.offset_invoices.length > 0) {
    offset_invoices = data.offset_invoices.map(normalizeOffsetRow);
  } else if (data.offset_invoice_no || data.offset_invoice_file) {
    offset_invoices = [
      createCitOffsetRow({
        number: String(data.offset_invoice_no ?? ''),
        file: normalizeAttachment(data.offset_invoice_file),
      }),
    ];
  }
  // Drop blank placeholder rows so unused offset section stays hidden.
  offset_invoices = offset_invoices.filter((row) => offsetRowHasAny(row) || row.stock_transfer_form);

  let clearing_invoices: CitClearingInvoiceRow[] = [];
  if (Array.isArray(data.clearing_invoices) && data.clearing_invoices.length > 0) {
    clearing_invoices = data.clearing_invoices
      .map(normalizeClearingInvoiceRow)
      .filter((row) => clearingInvoiceHasAny(row));
  }

  // Legacy rows (before needs_deposit field): infer Yes when deposit/offset content exists.
  if (needs_deposit === null) {
    const hasDepositContent = deposit_invoices.some(
      (row) =>
        row.number.trim() ||
        row.invoice_amount.trim() ||
        row.file ||
        row.lines.some((line) => line.item_no.trim() || line.quantity.trim()) ||
        row.payment_receipts.some((receipt) => receipt.number.trim() || receipt.file),
    );
    if (hasDepositContent || offset_invoices.length > 0) {
      needs_deposit = true;
    }
  }

  // If General has exactly one item, tag untagged deposit lines (legacy quantity imports).
  // If multiple General items share the same SOC qty and deposit qty is half, tag all (kit 50%).
  const generalFilled = items.filter((row) => row.item_no.trim());
  const generalCodes = generalFilled.map((row) => row.item_no.trim());
  const socQtys = generalFilled.map((row) => {
    const n = Number(row.sales_confirmation_qty);
    return Number.isFinite(n) ? n : null;
  });
  const sameSoc =
    socQtys.length > 1 &&
    socQtys.every((q) => q != null && q === socQtys[0]);

  deposit_invoices = deposit_invoices.map((row) => {
    const hasTagged = row.lines.some((line) => line.item_no.trim());
    if (hasTagged) return row;
    const hasAnyLine = row.lines.some((line) => line.item_no.trim() || line.quantity.trim());
    if (!hasAnyLine && !row.number.trim() && !row.invoice_amount.trim() && !row.file) {
      return row;
    }

    if (generalCodes.length === 1) {
      const onlyItem = generalCodes[0];
      return {
        ...row,
        lines: row.lines.map((line) =>
          line.item_no.trim() ? line : { ...line, item_no: onlyItem },
        ),
      };
    }

    if (sameSoc && socQtys[0] != null) {
      const depQty = Number(row.lines.find((line) => line.quantity.trim())?.quantity ?? '');
      if (Number.isFinite(depQty) && Math.abs(socQtys[0]! - depQty * 2) < 1e-9) {
        return {
          ...row,
          lines: generalCodes.map((item_no, index) =>
            createCitDepositLine({
              id: row.lines[index]?.id,
              item_no,
              quantity: String(depQty),
            }),
          ),
        };
      }
    }

    return row;
  });

  const customer_code = String(data.customer_code ?? '').trim();
  const customer_name = String(data.customer_name ?? data.customer ?? '').trim();

  return {
    customer_code,
    customer_name,
    status,
    sales_confirmation_no: String(data.sales_confirmation_no ?? ''),
    sales_confirmation_file: normalizeAttachment(data.sales_confirmation_file),
    needs_deposit,
    import_source,
    pbi_synced_at,
    items,
    deposit_invoices,
    offset_invoices,
    clearing_invoices,
  };
}

/** SC attachment present → row is confirmed and locked from PowerBI daily upsert. */
export function citHasSalesConfirmationAttachment(
  form: CustomiseItemsTransferFormData,
  pendingScFile?: File | null,
): boolean {
  return Boolean(form.sales_confirmation_file?.path?.trim() || pendingScFile);
}

/** Deposit / offset panels only after SC upload and explicit Yes. */
export function citShowDepositSections(
  form: CustomiseItemsTransferFormData,
  pendingScFile?: File | null,
): boolean {
  return citHasSalesConfirmationAttachment(form, pendingScFile) && form.needs_deposit === true;
}

/**
 * Tooling invoices (mock-up / block charge, $0) — available once Deposit arrangement
 * is chosen (Yes or No). Stored as clearing_invoices for backwards compatibility.
 */
export function citShowClearingInvoices(
  form: CustomiseItemsTransferFormData,
  pendingScFile?: File | null,
): boolean {
  return (
    citHasSalesConfirmationAttachment(form, pendingScFile) && form.needs_deposit !== null
  );
}

export function titleFromCit(form: CustomiseItemsTransferFormData): string {
  return (
    form.sales_confirmation_no.trim() ||
    form.customer_code.trim() ||
    form.customer_name.trim() ||
    form.items.find((row) => row.item_no.trim())?.item_no.trim() ||
    'Sales Confirmation'
  );
}

/** Client-side required-field checks before save. */
export function validateCitForm(
  form: CustomiseItemsTransferFormData,
  pending: {
    sales_confirmation: File | null;
    deposit: Record<string, File | null>;
    payment: Record<string, File | null>;
    offset: Record<string, File | null>;
    clearing?: Record<string, File | null>;
  },
): string | null {
  const enforce = CIT_ENFORCE_MANDATORY;
  const requireFiles = CIT_REQUIRE_SUPPORTING_FILES;
  const requirePaymentFile = CIT_REQUIRE_PAYMENT_SUPPORTING_FILE;
  const requireOffsetFile = CIT_REQUIRE_OFFSET_SUPPORTING_FILE;

  if (enforce && !form.customer_code.trim()) return 'Customer is required.';
  if (!form.sales_confirmation_no.trim()) return 'Sales confirmation no. is required.';
  if (enforce && !form.status.trim()) return 'Status is required.';

  const hasScFile = citHasSalesConfirmationAttachment(form, pending.sales_confirmation);
  if (hasScFile && form.needs_deposit === null) {
    return 'Select whether a deposit arrangement is needed.';
  }

  const filledItems = form.items.filter(
    (row) =>
      row.item_no.trim() ||
      row.sales_confirmation_qty.trim() ||
      row.uom.trim() ||
      row.description.trim(),
  );
  if (enforce && filledItems.length === 0) return 'Add at least one item.';
  for (const [index, row] of filledItems.entries()) {
    const label = `Item ${index + 1}`;
    if (!row.item_no.trim()) return `${label}: item no. is required.`;
    if (enforce && !row.sales_confirmation_qty.trim()) {
      return `${label}: sales confirmation quantity is required.`;
    }
    if (enforce && !row.uom.trim()) return `${label}: UOM is required.`;
  }

  const generalItemNos = new Set(
    form.items.map((row) => row.item_no.trim()).filter(Boolean),
  );

  if (citShowClearingInvoices(form, pending.sales_confirmation)) {
    const clearingPending = pending.clearing ?? {};
    const filledClearing = (form.clearing_invoices ?? []).filter((row) =>
      clearingInvoiceHasAny(row, clearingPending[row.id]),
    );
    for (const [index, row] of filledClearing.entries()) {
      if (!row.number.trim()) {
        return `Tooling invoice ${index + 1}: invoice no. is required.`;
      }
    }
  }

  if (!citShowDepositSections(form, pending.sales_confirmation)) {
    // No deposit path yet (or needs_deposit = No) — skip deposit/offset checks.
    return null;
  }

  const filledDeposits = form.deposit_invoices.filter(
    (row) =>
      row.number.trim() ||
      row.invoice_amount.trim() ||
      row.file ||
      pending.deposit[row.id] ||
      row.lines.some(depositLineHasAny) ||
      row.payment_receipts.some(
        (receipt) => receipt.number.trim() || receipt.file || pending.payment[receipt.id],
      ),
  );
  if (enforce && form.needs_deposit === true && filledDeposits.length === 0) {
    return 'Add at least one deposit invoice.';
  }
  for (const [index, row] of filledDeposits.entries()) {
    const label = `Deposit invoice ${index + 1}`;
    if (!row.number.trim()) return `${label}: number is required.`;
    if (enforce && !row.invoice_amount.trim()) return `${label}: invoice amount is required.`;
    if (requireFiles && !row.file && !pending.deposit[row.id]) {
      return `${label}: supporting file is required.`;
    }

    const filledLines = row.lines.filter(depositLineHasAny);
    if (enforce && filledLines.length === 0) return `${label}: add at least one item.`;
    const seenItems = new Set<string>();
    for (const [lineIndex, line] of filledLines.entries()) {
      const lineLabel = `${label} item ${lineIndex + 1}`;
      if (!line.item_no.trim()) return `${lineLabel}: item no. is required.`;
      if (generalItemNos.size > 0 && !generalItemNos.has(line.item_no.trim())) {
        return `${lineLabel}: item must be selected from General.`;
      }
      if (seenItems.has(line.item_no.trim())) {
        return `${label}: duplicate item ${line.item_no.trim()}.`;
      }
      seenItems.add(line.item_no.trim());
      if (enforce && !line.quantity.trim()) return `${lineLabel}: quantity is required.`;
      const qty = Number(line.quantity || 0);
      if (line.quantity.trim() && Number.isNaN(qty)) {
        return `${lineLabel}: quantity must be a number.`;
      }
      if (line.quantity.trim() && qty <= 0) {
        return `${lineLabel}: quantity must be greater than 0.`;
      }
    }

    for (const [receiptIndex, receipt] of row.payment_receipts.entries()) {
      const hasAny = Boolean(
        receipt.number.trim() || receipt.file || pending.payment[receipt.id],
      );
      if (!hasAny) continue;
      const receiptLabel = `${label} receipt/SCM ${receiptIndex + 1}`;
      if (!receipt.number.trim()) return `${receiptLabel}: number is required.`;
      if (requirePaymentFile && !receipt.file && !pending.payment[receipt.id]) {
        return `${receiptLabel}: supporting file is required.`;
      }
    }
  }

  for (const [index, row] of form.offset_invoices.entries()) {
    if (!offsetRowHasAny(row, pending.offset[row.id])) continue;
    const label = `Offset invoice ${index + 1}`;
    if (!row.number.trim()) return `${label}: number is required.`;
    if (enforce && !row.invoice_amount.trim()) return `${label}: invoice amount is required.`;
    if (requireOffsetFile && !row.file && !pending.offset[row.id]) {
      return `${label}: supporting file is required.`;
    }

    const filledLines = row.lines.filter(offsetLineHasAny);
    if (filledLines.length === 0) return `${label}: add at least one item.`;
    if (filledLines.length > 6) {
      return `${label}: at most 6 items can fit on the stock transfer form.`;
    }

    const seenItems = new Set<string>();
    for (const [lineIndex, line] of filledLines.entries()) {
      const lineLabel = `${label} item ${lineIndex + 1}`;
      if (!line.item_no.trim()) return `${lineLabel}: item no. is required.`;
      if (generalItemNos.size > 0 && !generalItemNos.has(line.item_no.trim())) {
        return `${lineLabel}: item must be selected from General.`;
      }
      if (seenItems.has(line.item_no.trim())) {
        return `${label}: duplicate item ${line.item_no.trim()}.`;
      }
      seenItems.add(line.item_no.trim());

      const defu = Number(line.warehouse_defu_qty || 0);
      const depot = Number(line.warehouse_depot_qty || 0);
      if (
        enforce &&
        !line.warehouse_defu_qty.trim() &&
        !line.warehouse_depot_qty.trim()
      ) {
        return `${lineLabel}: enter Defu and/or Depot warehouse qty.`;
      }
      if (
        (line.warehouse_defu_qty.trim() || line.warehouse_depot_qty.trim()) &&
        (Number.isNaN(defu) || Number.isNaN(depot))
      ) {
        return `${lineLabel}: warehouse quantities must be numbers.`;
      }
      if (defu < 0 || depot < 0) {
        return `${lineLabel}: warehouse quantities cannot be negative.`;
      }
      if (
        enforce &&
        (line.warehouse_defu_qty.trim() || line.warehouse_depot_qty.trim()) &&
        defu + depot <= 0
      ) {
        return `${lineLabel}: total quantity (Defu + Depot) must be greater than 0.`;
      }
    }
  }

  const overPaid = citOffsetOverPaidItems(form);
  if (overPaid.length > 0) {
    const details = overPaid
      .map(
        (row) =>
          `${row.itemNo} (offset ${formatCitQty(row.offsetQty)} > paid ${formatCitQty(row.paidQty)})`,
      )
      .join('; ');
    return `Offset qty exceeds paid qty: ${details}.`;
  }

  return null;
}

export async function allocateCitStockTransferNos(
  form: CustomiseItemsTransferFormData,
): Promise<CustomiseItemsTransferFormData> {
  // Import mode: keep manual stock transfer nos; do not auto-allocate.
  if (!CIT_ENFORCE_MANDATORY) return form;

  const db = getDbSupabase('forms');
  const nextOffsets: CitOffsetRow[] = [];
  for (const row of form.offset_invoices) {
    const needsNo = !row.stock_transfer_no.trim() && offsetRowHasAny(row);
    if (!needsNo) {
      nextOffsets.push(row);
      continue;
    }
    const { data, error } = await db.rpc('next_cit_stock_transfer_no');
    if (error) throw new Error(formatSupabaseError(error));
    nextOffsets.push({
      ...row,
      stock_transfer_no: String(data ?? '').trim(),
    });
  }
  return { ...form, offset_invoices: nextOffsets };
}

export async function fetchCitSubmissions(): Promise<ApplicationSubmission[]> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.from(TABLE).select('*').order('submitted_at', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => withCitSlug(row as ApplicationSubmission));
}

export async function fetchCitSubmission(id: string): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.from(TABLE).select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Submission not found.');
  return withCitSlug(data as ApplicationSubmission);
}

export async function submitCit(
  formData: CustomiseItemsTransferFormData,
  submittedBy?: string,
  submitterEmail?: string,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const payload = withCitAutoStatus(normalizeCitFormData(formData));
  const { data, error } = await db.rpc('submit_cit', {
    p_company_name: titleFromCit(payload),
    p_form_data: payload,
    p_submitted_by: submittedBy?.trim() || null,
    p_submitter_email: submitterEmail?.trim() || null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return withCitSlug(data as ApplicationSubmission);
}

export async function updateCit(
  id: string,
  formData: CustomiseItemsTransferFormData,
  options?: { skipAutoStatus?: boolean },
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const normalized = normalizeCitFormData(formData);
  const payload = options?.skipAutoStatus ? normalized : withCitAutoStatus(normalized);
  const { data, error } = await db.rpc('update_cit', {
    p_id: id,
    p_company_name: titleFromCit(payload),
    p_form_data: payload,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return withCitSlug(data as ApplicationSubmission);
}

export type CitAttachmentKind =
  | 'sales_confirmation'
  | 'deposit_invoice'
  | 'payment_receipt'
  | 'offset_invoice'
  | 'clearing_invoice';

export async function uploadCitAttachment(
  submissionId: string,
  kind: CitAttachmentKind,
  file: File,
  entryId?: string,
): Promise<CitAttachmentRef> {
  const db = getDbSupabase('forms');
  const safeName = file.name.replace(/[^\w.\-()+ ]+/g, '_').slice(0, 120) || kind;
  const folder = entryId ? `${kind}/${entryId}` : kind;
  const path = `${submissionId}/${folder}/${Date.now()}_${safeName}`;
  const { error } = await db.storage.from(BUCKET).upload(path, file, {
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw new Error(error.message || 'Failed to upload attachment.');
  return { path, fileName: file.name };
}

export async function getCitAttachmentSignedUrl(path: string): Promise<string> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.storage.from(BUCKET).createSignedUrl(path, 60 * 30);
  if (error) throw new Error(error.message || 'Failed to open attachment.');
  if (!data?.signedUrl) throw new Error('Failed to open attachment.');
  return data.signedUrl;
}

export async function downloadCitAttachment(path: string): Promise<Blob> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.storage.from(BUCKET).download(path.trim());
  if (error || !data) {
    throw new Error(error?.message || 'Failed to download attachment.');
  }
  return data;
}

/**
 * Supporting docs for Create form, in append order:
 * deposit1 → receipts for deposit1 → deposit2 → receipts… → all offset files → sales confirmation.
 * Does not include stock-transfer PDFs.
 */
export function collectCitSupportingAttachments(
  form: CustomiseItemsTransferFormData,
): CitAttachmentRef[] {
  const out: CitAttachmentRef[] = [];
  const push = (ref?: CitAttachmentRef) => {
    if (!ref?.path?.trim()) return;
    out.push(ref);
  };

  for (const deposit of form.deposit_invoices) {
    push(deposit.file);
    for (const receipt of deposit.payment_receipts) {
      push(receipt.file);
    }
  }
  for (const offset of form.offset_invoices) {
    push(offset.file);
  }
  for (const clearing of form.clearing_invoices ?? []) {
    push(clearing.file);
  }
  push(form.sales_confirmation_file);
  return out;
}
